import { employees as seedEmployees } from '../data/employees';
import { policies as seedPolicies, ptoRequests } from '../data/timeOff';
import type { DocFolder, Employee, Policy, ProfileDetails, PtoRequest, RecentFile } from '../types/hr';

/** Session-only, in-memory app state (no backend). */
export interface HRState {
  employees: Employee[];
  policies: Policy[];
  requests: PtoRequest[];
  details: Record<string, ProfileDetails>;
  files: Record<string, RecentFile[]>;
  folders: Record<string, DocFolder[]>;
}

let state: HRState = {
  employees: seedEmployees,
  policies: seedPolicies,
  requests: ptoRequests,
  details: {},
  files: {},
  folders: {},
};

const listeners = new Set<() => void>();

export function getHRState(): HRState {
  return state;
}

export function subscribeHR(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function update(updater: (s: HRState) => HRState) {
  state = updater(state);
  listeners.forEach((l) => l());
}

export const hrActions = {
  updateEmployee(id: string, patch: Partial<Employee>) {
    update((s) => ({ ...s, employees: s.employees.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));
  },
  saveDetails(id: string, details: ProfileDetails) {
    update((s) => ({ ...s, details: { ...s.details, [id]: details } }));
  },
  decideRequest(id: string, status: 'approved' | 'denied') {
    update((s) => ({ ...s, requests: s.requests.map((r) => (r.id === id ? { ...r, status } : r)) }));
  },
  addRequest(request: PtoRequest) {
    update((s) => ({ ...s, requests: [request, ...s.requests] }));
  },
  savePolicy(policy: Policy) {
    update((s) => ({
      ...s,
      policies: s.policies.some((p) => p.id === policy.id) ? s.policies.map((p) => (p.id === policy.id ? policy : p)) : [...s.policies, policy],
    }));
  },
  addFiles(employeeId: string, files: RecentFile[]) {
    update((s) => ({ ...s, files: { ...s.files, [employeeId]: [...files, ...(s.files[employeeId] ?? [])] } }));
  },
  addFolder(employeeId: string, folder: DocFolder) {
    update((s) => ({ ...s, folders: { ...s.folders, [employeeId]: [...(s.folders[employeeId] ?? []), folder] } }));
  },
};
